import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Upload, CheckCircle2, AlertCircle, FileText, Shield, X,
} from 'lucide-react';
import { formatDate, formatDateTime } from './kit/time';
import PickSelect from './shared/PickSelect';
import { LINK_PAUSED_COPY, isLinkPaused, publicLinkError } from './errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BorrowerUploadPage() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]); // [{ filename, message }]
  const [forRequest, setForRequest] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/upload-public/${token}`)
      .then((r) => { setReq(r.data); setUploadedFiles(r.data.uploads || []); })
      .catch((e) => setError(publicLinkError(e, 'Upload link is invalid or expired')))
      .finally(() => setLoading(false));
  }, [token]);

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setFailedFiles([]);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (forRequest) fd.append('doc_request_id', forRequest);
    try {
      const { data } = await axios.post(`${API}/upload-public/${token}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFiles((prev) => [...prev, ...(data.uploads || [])]);
      setForRequest(''); // the next file is a new choice, not the same document again
      setFailedFiles((data.failed || []).map((f) => ({
        filename: f.filename,
        message: 'Could not be saved. Please try this file again.',
      })));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = isLinkPaused(e) ? LINK_PAUSED_COPY
        : typeof detail === 'string' ? detail : 'Upload failed. Please try again.';
      setFailedFiles(files.map((f) => ({ filename: f.name, message })));
    }
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F8FC' }}>
      <Loader2 size={30} className="animate-spin text-slate-400" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F7F8FC' }}>
      <div className="max-w-md text-center bg-white rounded-2xl p-8 border border-slate-200">
        <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
        <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Link Unavailable</div>
        <p className="text-slate-500 mt-2 text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #F7F8FC 0%, #EEF0F8 100%)' }}>
      <header className="border-b" style={{ background: '#070B5F' }}>
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #6BA82E 50%, transparent 100%)' }} />
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-bold text-white">Top Choice Lending</div>
            <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#A6E56C' }}>Secure Document Upload</div>
          </div>
          <div className="text-right text-white/80 text-[11px]">
            Requested by <b className="text-white">{req.requested_by_name}</b><br />
            {req.expires_at ? `Expires ${formatDate(req.expires_at)}` : 'Valid while your loan is open'}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Hi {req.borrower_name},</div>
          <p className="text-slate-600 mt-2">{req.reason || 'Please upload the following documents to help us verify your income for underwriting.'}</p>
        </div>

        {/* Docs checklist */}
        {req.doc_types_needed?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
            <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>Documents Needed</div>
            <ul className="space-y-2">
              {req.doc_types_needed.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px]" style={{ color: '#070B5F' }}>
                  <FileText size={13} className="mt-0.5 shrink-0" style={{ color: '#6BA82E' }} />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* One link, several requests: say which one these files are for. A
            link for a single request needs no choice (the server links it). */}
        {req.requests?.length > 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
            <PickSelect
              label="Which document is this for?"
              labelClassName="text-[11px] uppercase tracking-widest font-bold mb-2 block"
              value={forRequest}
              onChange={setForRequest}
              options={req.requests.map((r) => ({ value: r.doc_request_id, label: r.label }))}
              placeholder="Choose one…"
              testId="borrower-request-select"
            />
          </div>
        )}

        {/* Drop zone */}
        <div
          className="rounded-2xl border-2 border-dashed p-10 text-center transition-colors"
          style={{ borderColor: dragging ? '#6BA82E' : '#cbd5e1', background: dragging ? '#F3FAE8' : '#fff' }}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
          data-testid="borrower-drop-zone"
        >
          <input ref={inputRef} type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.heic,.heif" onChange={(e) => doUpload(e.target.files)} data-testid="borrower-file-input" />
          {uploading ? (
            <>
              <Loader2 size={36} className="animate-spin mx-auto mb-2" style={{ color: '#6BA82E' }} />
              <div className="font-semibold" style={{ color: '#070B5F' }}>Uploading securely…</div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: '#F3FAE8' }}>
                <Upload size={26} style={{ color: '#6BA82E' }} />
              </div>
              <div className="font-serif text-lg font-semibold" style={{ color: '#070B5F' }}>
                {dragging ? 'Release to upload' : 'Drag & drop, or click to browse'}
              </div>
              <div className="text-xs text-slate-500 mt-1">PDF, PNG, JPG, HEIC — up to 25 MB each</div>
              <div className="text-[10px] text-slate-400 mt-2 flex items-center justify-center gap-1"><Shield size={9} /> Encrypted transfer · SHA-256 audit hash</div>
            </>
          )}
        </div>

        {failedFiles.length > 0 && (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-5" role="alert" data-testid="borrower-failed-files">
            <div className="text-[13px] font-bold text-red-700 mb-2">These files were not saved</div>
            <ul className="space-y-1">
              {failedFiles.map((f, i) => (
                <li key={i} className="text-[13px] text-red-700" data-testid={`failed-${i}`}>
                  <b>{f.filename}</b> — {f.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploaded list */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: '#6BA82E' }}>
              Uploaded ({uploadedFiles.length})
            </div>
            <div className="space-y-2">
              {uploadedFiles.map((u, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100" data-testid={`uploaded-${i}`}>
                  <CheckCircle2 size={14} style={{ color: '#6BA82E' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: '#070B5F' }}>{u.filename || u.original_filename}</div>
                    <div className="text-[10px] text-slate-400">{u.uploaded_at ? formatDateTime(u.uploaded_at) : 'just now'}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 rounded-lg text-[12px] flex items-start gap-2" style={{ background: 'rgba(107,168,46,0.08)', color: '#3a5717' }}>
              <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
              <span>Great — we've received your documents. Your loan officer has been notified and will reach out with any next steps.</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
